import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

const responseSchema = z.object({
  message: z.string(),
});

type Response = z.infer<typeof responseSchema>;

export const FeedbackSchema = z.object({
  feedback: z.union([z.literal("like"), z.literal("dislike")]).nullable(),
  messageId: z.string(),
});

export type FeedbackData = z.infer<typeof FeedbackSchema>;
export type Feedback = z.infer<typeof FeedbackSchema>["feedback"];

export const addFeedback = async ({
  lang,
  token,
  feedback,
  messageId,
}: {
  lang: Lang;
  token: string;
  feedback: Feedback;
  messageId: string;
}): Promise<Response> => {
  const endpoint = `/winwin-gpt/feedback`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Accept-Language": lang,
      "X-User-Profile": token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ feedback, messageId }),
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseSchema.parse(await res.json());
};

export const useAddFeedback = () => {
  const { lang } = useLang();
  const { backendToken } = useAuth();

  return useMutation((data: FeedbackData) =>
    addFeedback({ lang, token: backendToken ?? "", ...data })
  );
};
